import BidThemeContext from "@/contexts/BidThemeContext";
import { Theme } from "@mui/material/styles/createThemeNoVars";
import ThemeProvider from "@mui/material/styles/ThemeProvider";
import { ReactNode, useEffect, useState } from "react";
import bidTechLogoUrl from "@/assets/bidtech-full-logo-192x44.webp";
import bidTechFaviconUrl from "@/assets/bidtech-logo-512x512.webp";
import { extendCurrentThemeColors } from "@/lib/utils";
import axios from "@/axios";
import { isAxiosError } from "axios";
import { ThemeColors, ThemeResponse } from "@/types/theme";
import * as sentry from "@sentry/react";
import { useToast } from "@/hooks/useToast";
import { useQuery } from "@tanstack/react-query";
import useMe from "@/hooks/useMe";

interface BidThemeProviderProps {
  children: ReactNode;
  theme: Theme;
}

function makeThemeColorsFromResponse(response: ThemeResponse): ThemeColors {
  return {
    primary: response.primary,
    error: response.error,
    text: response.text,
    success: response.success,
    warning: response.warning,
    contrastText: response.contrast_text,
  };
}

async function fetchTheme(): Promise<ThemeResponse | null> {
  try {
    const { data } = await axios.get<ThemeResponse>("/bff/api/pages/theme/");
    return data;
  } catch (error: unknown) {
    if (isAxiosError(error) && error.response?.status === 404) {
      console.log("No custom theme found. Using default theme.");
    } else {
      sentry.captureException(error);
    }

    throw error;
  }
}

function useBidThemeProvider({ theme }: { theme: Theme }) {
  const [currentLogo, setNewLogo] = useState(bidTechLogoUrl);
  const [currentFavicon, setNewFavicon] = useState(bidTechFaviconUrl);
  const [currentTheme, setNewTheme] = useState(theme);
  const [currentPwaName, setNewPwaName] = useState("Tá Quitado");
  const [userToken, setUserToken] = useState<string | null>(null);
  const { toast } = useToast();
  const meQuery = useMe();

  const themeQuery = useQuery({
    queryKey: ["theme", userToken],
    queryFn: fetchTheme,
    enabled: userToken !== null,
    retry: false,
  });

  useEffect(() => {
    const token = localStorage.getItem("accessToken");
    if (token) {
      setUserToken(token);
    }
  });

  useEffect(() => {
    if (!themeQuery.isFetched || !meQuery.isSuccess) {
      return;
    }

    const data = themeQuery.data;

    if (themeQuery.isSuccess) {
      if (data) {
        const newColors = makeThemeColorsFromResponse(data);
        setNewTheme(extendCurrentThemeColors(newColors));
        setNewPwaName(data.pwa_name || "Tá Quitado");
        setNewLogo(data.logo || bidTechLogoUrl);
        setNewFavicon(data.favicon || bidTechFaviconUrl);
      }
    }

    if (meQuery.meData?.parceiro?.id) {
      return document
        .getElementById("manifest-placeholder")!
        .setAttribute(
          "href",
          `${import.meta.env.VITE_BACKEND_BASE_URL}/bff/api/pwa/${
            meQuery.meData?.parceiro?.id
          }/manifest.json`
        );
    }

    document
      .getElementById("manifest-placeholder")!
      .setAttribute(
        "href",
        `${import.meta.env.VITE_BACKEND_BASE_URL}/bff/api/pwa/manifest.json`
      );
  }, [themeQuery.isFetched, meQuery.isFetched]);

  useEffect(() => {
    if (themeQuery.isError) {
      const wasWarned = localStorage.getItem("warnedFailingTheme") === "true";

      if (!wasWarned) {
        toast({
          title: "Erro em tema personalizado",
          description: "Ocorreu um erro ao carregar seu tema personalizado.",
          variant: "destructive",
        });
        localStorage.setItem("warnedFailingTheme", "true");
      }

      setNewTheme(theme);
      setNewPwaName("Tá Quitado");
      setNewLogo(bidTechLogoUrl);
      setNewFavicon(bidTechFaviconUrl);
    }
  }, [themeQuery.isError]);

  return {
    currentPwaName,
    setNewPwaName,
    currentTheme,
    setNewTheme,
    currentLogo,
    setNewLogo,
    currentFavicon,
    setNewFavicon,
  };
}

export default function BidThemeProvider({
  children,
  theme,
}: BidThemeProviderProps) {
  const {
    currentPwaName,
    setNewPwaName,
    currentTheme,
    setNewTheme,
    currentLogo,
    setNewLogo,
    currentFavicon,
    setNewFavicon,
  } = useBidThemeProvider({ theme });

  return (
    <BidThemeContext.Provider
      value={{
        pwaName: currentPwaName,
        setPwaName: setNewPwaName,
        theme: currentTheme,
        setTheme: setNewTheme,
        logo: currentLogo,
        setLogo: setNewLogo,
        favicon: currentFavicon,
        setFavicon: setNewFavicon,
      }}
    >
      <ThemeProvider theme={currentTheme}>{children}</ThemeProvider>
    </BidThemeContext.Provider>
  );
}
